import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Grid, Card, CardMedia, CardContent,
  CardActions, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, Select, MenuItem,
  FormControl, InputLabel, Alert, CircularProgress, Snackbar,
  Tooltip, Divider, Paper, Tabs, Tab, Avatar, List, ListItem,
  ListItemAvatar, ListItemText, ListItemSecondaryAction, Autocomplete,
} from '@mui/material';
import {
  Add, Edit, Delete, DragIndicator,
  ToggleOn, ToggleOff, CloudUpload, Image as ImageIcon, ShoppingBag,
} from '@mui/icons-material';
import {
  fetchHeroBanners, createHeroBanner, updateHeroBanner,
  deleteHeroBanner, toggleHeroBanner, reorderHeroBanners,
  fetchHeroProducts, addHeroProduct, removeHeroProduct, reorderHeroProducts,
  fetchProducts,
} from '../../api/api';
import api from '../../api/api';
import { io } from 'socket.io-client';

const EMPTY_FORM = {
  title: '', subtitle: '', imageUrl: '', ctaText: '',
  ctaAction: 'none', ctaTarget: '', backgroundColor: '#667eea',
  textColor: '#ffffff', startDate: '', endDate: '', isActive: true,
};

export default function ContentManager() {
  const [tab, setTab] = useState(0);

  // ── Hero Banners state ───────────────────────────────────────
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editBanner, setEditBanner] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  // DnD state
  const [dragId, setDragId] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [hoverSide, setHoverSide] = useState(null);

  // ── Hero Products state ──────────────────────────────────────
  const [heroProducts, setHeroProducts] = useState([]);
  const [hpLoading, setHpLoading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [hpDragId, setHpDragId] = useState(null);
  const [hpHoverId, setHpHoverId] = useState(null);
  const [hpHoverSide, setHpHoverSide] = useState(null);

  const loadHeroProducts = useCallback(async () => {
    setHpLoading(true);
    try {
      const res = await fetchHeroProducts();
      setHeroProducts(res.data || []);
    } catch { } finally { setHpLoading(false); }
  }, []);

  const loadAllProducts = useCallback(async () => {
    try {
      const res = await fetchProducts();
      const d = res.data;
      setAllProducts(Array.isArray(d) ? d : d?.products ?? d?.data ?? d?.items ?? []);
    } catch { }
  }, []);

  useEffect(() => { loadHeroProducts(); loadAllProducts(); }, [loadHeroProducts, loadAllProducts]);

  const handleAddHeroProduct = async () => {
    if (!selectedProduct) return;
    try {
      await addHeroProduct(selectedProduct._id);
      setSelectedProduct(null);
      setProductSearch('');
      showSnack('Product added to hero carousel');
      loadHeroProducts();
    } catch (e) {
      showSnack(e.response?.data?.message || 'Failed to add product', 'error');
    }
  };

  const handleRemoveHeroProduct = async (id) => {
    try {
      await removeHeroProduct(id);
      showSnack('Product removed');
      loadHeroProducts();
    } catch { showSnack('Failed to remove', 'error'); }
  };

  const onHpDragStart = (e, id) => { setHpDragId(id); e.dataTransfer.effectAllowed = 'move'; };
  const onHpDragOver = (e, id) => {
    e.preventDefault();
    if (id === hpDragId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setHpHoverId(id);
    setHpHoverSide(e.clientY < rect.top + rect.height / 2 ? 'top' : 'bottom');
  };
  const onHpDragLeave = (e, id) => { if (!e.currentTarget.contains(e.relatedTarget) && hpHoverId === id) { setHpHoverId(null); setHpHoverSide(null); } };
  const onHpDrop = async (e, targetId) => {
    e.preventDefault();
    setHpHoverId(null); setHpHoverSide(null);
    if (!hpDragId || hpDragId === targetId) return;
    const arr = [...heroProducts];
    const from = arr.findIndex(i => i._id === hpDragId);
    const to = arr.findIndex(i => i._id === targetId);
    if (from === -1 || to === -1) return;
    const [moved] = arr.splice(from, 1);
    const at = hpHoverSide === 'bottom' ? to + (from < to ? 0 : 1) : to - (from > to ? 0 : 1);
    arr.splice(Math.max(0, at), 0, moved);
    setHeroProducts(arr);
    try {
      await reorderHeroProducts(arr.map((item, i) => ({ id: item._id, position: i })));
    } catch { showSnack('Reorder failed', 'error'); loadHeroProducts(); }
  };
  const onHpDragEnd = () => { setHpDragId(null); setHpHoverId(null); setHpHoverSide(null); };

  // Filter products: exclude already-added ones, then apply search text
  const addedProductIds = new Set(heroProducts.map(hp => hp.product?._id || hp.product));
  const availableProducts = useMemo(() => {
    const notAdded = allProducts.filter(p => !addedProductIds.has(p._id));
    if (!productSearch.trim()) return notAdded;
    const q = productSearch.toLowerCase();
    return notAdded.filter(p => p.title?.toLowerCase().includes(q));
  }, [allProducts, addedProductIds, productSearch]);

  // Socket suppress — increment before each write so our own emit doesn't reload
  const suppressSocket = useRef(0);
  const skipNext = () => { suppressSocket.current += 1; };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetchHeroBanners();
      setBanners(res.data?.banners || res.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load banners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Socket listener for live updates
  useEffect(() => {
    const socket = io(api.defaults.baseURL, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });
    socket.on('hero:updated', () => {
      if (suppressSocket.current > 0) { suppressSocket.current -= 1; return; }
      load();
    });
    socket.on('heroProducts:updated', () => loadHeroProducts());
    return () => { socket.off('hero:updated'); socket.off('heroProducts:updated'); socket.disconnect(); };
  }, [load, loadHeroProducts]);

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const openAdd = () => {
    setEditBanner(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (banner) => {
    setEditBanner(banner);
    setForm({
      title: banner.title || '',
      subtitle: banner.subtitle || '',
      imageUrl: banner.imageUrl || '',
      ctaText: banner.ctaText || '',
      ctaAction: banner.ctaAction || 'none',
      ctaTarget: banner.ctaTarget || '',
      backgroundColor: banner.backgroundColor || '#667eea',
      textColor: banner.textColor || '#ffffff',
      startDate: banner.startDate ? banner.startDate.slice(0, 16) : '',
      endDate: banner.endDate ? banner.endDate.slice(0, 16) : '',
      isActive: banner.isActive !== false,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) return showSnack('Title is required', 'error');
    setSaving(true);
    skipNext();
    try {
      const payload = { ...form };
      if (!payload.startDate) delete payload.startDate;
      if (!payload.endDate) delete payload.endDate;
      if (payload.ctaAction === 'none') delete payload.ctaTarget;
      if (editBanner) {
        await updateHeroBanner(editBanner._id, payload);
        showSnack('Banner updated');
      } else {
        await createHeroBanner(payload);
        showSnack('Banner created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      suppressSocket.current = Math.max(0, suppressSocket.current - 1);
      showSnack(e.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    skipNext();
    try {
      await toggleHeroBanner(id);
      showSnack('Banner toggled');
      load();
    } catch (e) {
      suppressSocket.current = Math.max(0, suppressSocket.current - 1);
      showSnack('Toggle failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    skipNext();
    try {
      await deleteHeroBanner(id);
      setDeleteConfirm(null);
      showSnack('Banner deleted');
      load();
    } catch (e) {
      suppressSocket.current = Math.max(0, suppressSocket.current - 1);
      showSnack('Delete failed', 'error');
    }
  };

  // ── Drag and Drop ────────────────────────────────────────────
  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id === dragId) { setHoverId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const side = e.clientY < rect.top + rect.height / 2 ? 'top' : 'bottom';
    setHoverId(id);
    setHoverSide(side);
  };

  const onDragLeave = (e, id) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (hoverId === id) { setHoverId(null); setHoverSide(null); }
  };

  const onDrop = async (e, targetId) => {
    e.preventDefault();
    setHoverId(null);
    setHoverSide(null);
    if (!dragId || dragId === targetId) return;

    const arr = [...banners];
    const fromIdx = arr.findIndex(b => b._id === dragId);
    const toIdx   = arr.findIndex(b => b._id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;

    const [moved] = arr.splice(fromIdx, 1);
    const insertAt = hoverSide === 'bottom' ? toIdx + (fromIdx < toIdx ? 0 : 1) : toIdx - (fromIdx > toIdx ? 0 : 1);
    arr.splice(Math.max(0, insertAt), 0, moved);

    setBanners(arr);
    skipNext();
    try {
      const order = arr.map((b, i) => ({ id: b._id, position: i }));
      await reorderHeroBanners(order);
    } catch {
      showSnack('Reorder failed', 'error');
      suppressSocket.current = Math.max(0, suppressSocket.current - 1);
      load();
    }
  };

  const onDragEnd = () => { setDragId(null); setHoverId(null); setHoverSide(null); };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    setUploading(true);
    try {
      const res = await api.post('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data?.url || res.data?.imageUrl || '';
      setForm(f => ({ ...f, imageUrl: url }));
      showSnack('Image uploaded');
    } catch {
      showSnack('Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const dropIndicator = (id, side) =>
    hoverId === id && hoverSide === side
      ? `inset 0 ${side === 'top' ? '3' : '-3'}px 0 0 #667eea`
      : 'none';

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="text.primary">Content Manager</Typography>
          <Typography variant="body2" color="text.secondary">Drag to reorder — changes apply live</Typography>
        </Box>
        {tab === 0 && (
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700 }}>
            Add Banner
          </Button>
        )}
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Hero Banners" icon={<ImageIcon fontSize="small" />} iconPosition="start" />
        <Tab label="Featured Products" icon={<ShoppingBag fontSize="small" />} iconPosition="start" />
      </Tabs>

      {/* ── TAB 0: HERO BANNERS ── */}
      {tab === 0 && (<>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && banners.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.paper' }}>
          <ImageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No banners yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Add your first hero banner to get started</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            Add Banner
          </Button>
        </Paper>
      )}

      {!loading && banners.length > 0 && (
        <Grid container spacing={2}>
          {banners.map((banner, index) => (
            <Grid item xs={12} md={6} key={banner._id}>
              <Card
                draggable
                onDragStart={e => onDragStart(e, banner._id)}
                onDragOver={e => onDragOver(e, banner._id)}
                onDragLeave={e => onDragLeave(e, banner._id)}
                onDrop={e => onDrop(e, banner._id)}
                onDragEnd={onDragEnd}
                sx={{
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  overflow: 'hidden',
                  cursor: 'grab',
                  opacity: dragId === banner._id ? 0.45 : 1,
                  boxShadow: `${dropIndicator(banner._id, 'top')}, ${dropIndicator(banner._id, 'bottom')}`.replace('none, ', '').replace(', none', '') || 'none',
                  transition: 'opacity 0.15s, box-shadow 0.12s',
                }}
              >
                {banner.imageUrl ? (
                  <CardMedia
                    component="img"
                    height="160"
                    image={banner.imageUrl}
                    alt={banner.title}
                    sx={{ objectFit: 'cover', pointerEvents: 'none' }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <Box sx={{ height: 160, bgcolor: banner.backgroundColor || '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <Typography sx={{ color: banner.textColor || '#fff', fontWeight: 700, fontSize: 18 }}>{banner.title}</Typography>
                  </Box>
                )}
                <CardContent sx={{ pb: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <DragIndicator sx={{ fontSize: 16, color: 'text.disabled' }} />
                    <Chip
                      label={`#${index + 1}`}
                      size="small"
                      sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', fontWeight: 700 }}
                    />
                    <Chip
                      label={banner.active !== false ? 'Active' : 'Inactive'}
                      size="small"
                      color={banner.active !== false ? 'success' : 'default'}
                    />
                    {banner.ctaAction && banner.ctaAction !== 'none' && (
                      <Chip label={banner.ctaAction} size="small" variant="outlined" />
                    )}
                  </Box>
                  <Typography fontWeight={700} color="text.primary" noWrap>{banner.title}</Typography>
                  {banner.subtitle && (
                    <Typography variant="body2" color="text.secondary" noWrap>{banner.subtitle}</Typography>
                  )}
                  {banner.ctaText && (
                    <Typography variant="caption" color="text.secondary">CTA: {banner.ctaText}</Typography>
                  )}
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, justifyContent: 'flex-end' }}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title={banner.active !== false ? 'Deactivate' : 'Activate'}>
                      <IconButton size="small" onClick={() => handleToggle(banner._id)} color={banner.active !== false ? 'success' : 'default'}>
                        {banner.active !== false ? <ToggleOn /> : <ToggleOff />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(banner)} color="primary">
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDeleteConfirm(banner)} color="error">
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{editBanner ? 'Edit Banner' : 'Add Banner'}</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} fullWidth />
            <TextField label="Subtitle" value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))} fullWidth />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                label="Image URL"
                value={form.imageUrl}
                onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
                fullWidth
              />
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileUpload} />
              <Button
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                sx={{ whiteSpace: 'nowrap', minWidth: 120 }}
              >
                Upload
              </Button>
            </Box>
            {form.imageUrl && (
              <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                <img src={form.imageUrl} alt="Preview" style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                  onError={e => { e.target.style.display = 'none'; }} />
              </Box>
            )}
            <TextField label="CTA Text" value={form.ctaText} onChange={e => setForm(f => ({ ...f, ctaText: e.target.value }))} fullWidth />
            <FormControl fullWidth>
              <InputLabel>CTA Action</InputLabel>
              <Select value={form.ctaAction} label="CTA Action" onChange={e => setForm(f => ({ ...f, ctaAction: e.target.value }))}>
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="product">Product</MenuItem>
                <MenuItem value="category">Category</MenuItem>
                <MenuItem value="screen">Screen</MenuItem>
                <MenuItem value="url">URL</MenuItem>
              </Select>
            </FormControl>
            {form.ctaAction !== 'none' && (
              <TextField label="CTA Target" value={form.ctaTarget} onChange={e => setForm(f => ({ ...f, ctaTarget: e.target.value }))} fullWidth
                placeholder={form.ctaAction === 'url' ? 'https://...' : 'ID or screen name'} />
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">Background Color</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <input type="color" value={form.backgroundColor} onChange={e => setForm(f => ({ ...f, backgroundColor: e.target.value }))}
                    style={{ width: 40, height: 36, border: 'none', borderRadius: 4, cursor: 'pointer' }} />
                  <TextField size="small" value={form.backgroundColor} onChange={e => setForm(f => ({ ...f, backgroundColor: e.target.value }))} sx={{ flex: 1 }} />
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">Text Color</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <input type="color" value={form.textColor} onChange={e => setForm(f => ({ ...f, textColor: e.target.value }))}
                    style={{ width: 40, height: 36, border: 'none', borderRadius: 4, cursor: 'pointer' }} />
                  <TextField size="small" value={form.textColor} onChange={e => setForm(f => ({ ...f, textColor: e.target.value }))} sx={{ flex: 1 }} />
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Start Date" type="datetime-local" value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} fullWidth />
              <TextField label="End Date" type="datetime-local" value={form.endDate}
                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} fullWidth />
            </Box>
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} /> : (editBanner ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={800}>Delete Banner</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <strong>{deleteConfirm?.title}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm?._id)}>Delete</Button>
        </DialogActions>
      </Dialog>
      </>)}

      {/* ── TAB 1: FEATURED PRODUCTS ── */}
      {tab === 1 && (
        <Box>
          {/* Product picker */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography fontWeight={700} mb={1.5}>Add Product to Hero Carousel</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Autocomplete
                sx={{ flex: 1 }}
                options={availableProducts}
                getOptionLabel={p => p.title || ''}
                filterOptions={(x) => x}
                value={selectedProduct}
                onChange={(_, v) => setSelectedProduct(v)}
                inputValue={productSearch}
                onInputChange={(_, v) => setProductSearch(v)}
                noOptionsText={allProducts.length === 0 ? 'Loading products…' : 'No products found'}
                renderOption={(props, p) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {p.images?.[0] && <Avatar src={p.images[0]} variant="rounded" sx={{ width: 32, height: 32 }} />}
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{p.title}</Typography>
                      <Typography variant="caption" color="text.secondary">KSh {p.price?.toLocaleString()}</Typography>
                    </Box>
                  </Box>
                )}
                renderInput={params => <TextField {...params} label="Search products" size="small" />}
              />
              <Button variant="contained" startIcon={<Add />} onClick={handleAddHeroProduct}
                disabled={!selectedProduct}
                sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                Add
              </Button>
            </Box>
          </Paper>

          {hpLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}

          {!hpLoading && heroProducts.length === 0 && (
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
              <ShoppingBag sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No featured products</Typography>
              <Typography variant="body2" color="text.secondary">Search and add products above to feature them in the hero carousel</Typography>
            </Paper>
          )}

          {!hpLoading && heroProducts.length > 0 && (
            <Paper sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <List disablePadding>
                {heroProducts.map((item, index) => {
                  const product = item.product ?? {};
                  const img = product.images?.[0];
                  const isHover = hpHoverId === item._id;
                  return (
                    <ListItem
                      key={item._id}
                      draggable
                      onDragStart={e => onHpDragStart(e, item._id)}
                      onDragOver={e => onHpDragOver(e, item._id)}
                      onDragLeave={e => onHpDragLeave(e, item._id)}
                      onDrop={e => onHpDrop(e, item._id)}
                      onDragEnd={onHpDragEnd}
                      divider={index < heroProducts.length - 1}
                      sx={{
                        cursor: 'grab',
                        opacity: hpDragId === item._id ? 0.4 : 1,
                        borderTop: isHover && hpHoverSide === 'top' ? '2px solid #667eea' : undefined,
                        borderBottom: isHover && hpHoverSide === 'bottom' ? '2px solid #667eea' : undefined,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <DragIndicator sx={{ color: 'text.disabled', mr: 1, fontSize: 18 }} />
                      <Chip label={`#${index + 1}`} size="small"
                        sx={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff', fontWeight: 700, mr: 1.5, minWidth: 36 }} />
                      <ListItemAvatar>
                        {img
                          ? <Avatar src={img} variant="rounded" sx={{ width: 48, height: 48 }} />
                          : <Avatar variant="rounded" sx={{ width: 48, height: 48, bgcolor: '#667eea' }}><ShoppingBag /></Avatar>
                        }
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography fontWeight={700} noWrap>{product.title || '—'}</Typography>}
                        secondary={`KSh ${(product.price || 0).toLocaleString()}`}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" onClick={() => handleRemoveHeroProduct(item._id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          )}
        </Box>
      )}

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
